import { useCallback, useEffect, useState } from "react";
import { classifyCaringNoteError, CaringNoteErrorKind } from "services/caringnoteApi";

/**
 * 케어링노트 열람 데이터 로딩 훅 (계획 §3-3 · T3/T4 공통).
 *
 * ⚠️ 오류 시 **로그인으로 보내지 않는다** — `classifyCaringNoteError`로 화면 상태만 만든다.
 * ⚠️ 언마운트 후 setState 방지(alive 가드). 재시도는 GET 재조회(idempotent).
 */
export type CaringNotePhase<T> =
  | { phase: "loading" }
  | { phase: "empty" }
  | { phase: "error"; kind: CaringNoteErrorKind }
  | { phase: "ready"; data: T };

export function useCaringNoteReport<T extends { content: string }>(
  fetcher: (token: string) => Promise<T>,
  token: string | undefined
): { state: CaringNotePhase<T>; retry: () => void } {
  const [state, setState] = useState<CaringNotePhase<T>>({ phase: "loading" });
  const [nonce, setNonce] = useState(0);

  const retry = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let alive = true;

    if (!token) {
      // 토큰이 없으면 없는 링크와 동일하게 처리(로그인 유도 금지).
      setState({ phase: "error", kind: "notFound" });
      return;
    }

    setState({ phase: "loading" });
    fetcher(token)
      .then((data) => {
        if (!alive) return;
        if (!data || typeof data.content !== "string" || data.content.trim() === "") {
          // 분석 미완 — 리뷰는 있으나 본문이 비어있다.
          setState({ phase: "empty" });
          return;
        }
        setState({ phase: "ready", data });
      })
      .catch((err) => {
        if (!alive) return;
        setState({ phase: "error", kind: classifyCaringNoteError(err).kind });
      });

    return () => {
      alive = false;
    };
  }, [fetcher, token, nonce]);

  return { state, retry };
}
